/**
 * Procurement facets.
 *
 * `toFilter` and `toWhere` express the same filter in two languages — the
 * structured input `tenderFacets` takes, and the input @neo4j/graphql
 * generates for the paged `tenders` field. They must agree, or the
 * sidebar's counts promise a number of rows the table will not deliver.
 * tests/facets_consistency.mjs runs this module against the live API to
 * hold them together.
 */

import type { FacetSet, TaxonomyBranch } from './types';

/* CPV divisions grouped into sectors a reader recognises.

   The two-digit division is the level worth filtering on — the full
   eight-digit code is far too granular to choose from — but a division
   number means nothing on its own, so they are gathered under named
   sectors with the divisions kept reachable underneath. */
export const CPV_SECTORS: readonly TaxonomyBranch[] = [
  { key: 'build', label: 'Строителство и инфраструктура', leaves: ['45', '44', '71'] },
  { key: 'health', label: 'Здравеопазване и социални дейности', leaves: ['33', '85'] },
  { key: 'food', label: 'Храни и земеделие', leaves: ['03', '15', '16', '77', '05'] },
  { key: 'energy', label: 'Енергетика, горива и суровини', leaves: ['09', '14'] },
  { key: 'it', label: 'ИТ, телекомуникации и електроника', leaves: ['30', '32', '48', '72', '64'] },
  { key: 'transport', label: 'Транспорт и логистика', leaves: ['34', '60', '63'] },
  { key: 'industry', label: 'Индустрия, машини и поддръжка', leaves: ['19', '24', '31', '38', '42', '43', '50', '51'] },
  { key: 'environment', label: 'Околна среда и комунални услуги', leaves: ['41', '65', '90'] },
  { key: 'business', label: 'Бизнес, финанси и администрация', leaves: ['66', '70', '75', '79'] },
  { key: 'security', label: 'Сигурност и отбрана', leaves: ['35', '76'] },
  { key: 'goods', label: 'Стоки, обзавеждане и потребление', leaves: ['18', '22', '37', '39', '55'] },
  { key: 'education', label: 'Образование, култура и услуги', leaves: ['73', '80', '92', '98'] },
];

export const DIVISION_LABELS: Record<string, string> = {
  '03': 'Земеделие и растениевъдство', '05': 'Риба и рибни продукти',
  '09': 'Нефт, газ, електроенергия', '14': 'Минерали и основни метали',
  '15': 'Хранителни продукти и напитки', '16': 'Земеделска техника',
  '18': 'Облекло и обувки', '19': 'Кожа, текстил, пластмаси',
  '22': 'Печатни материали', '24': 'Химически продукти',
  '30': 'Офис и изчислителна техника', '31': 'Електрически машини',
  '32': 'Радио- и телекомуникационна техника', '33': 'Медицинско оборудване и лекарства',
  '34': 'Транспортно оборудване', '35': 'Оборудване за сигурност и отбрана',
  '37': 'Музикални и спортни стоки', '38': 'Лабораторна и оптична техника',
  '39': 'Мебели и обзавеждане', '41': 'Събрана и пречистена вода',
  '42': 'Промишлени машини', '43': 'Машини за миннодобив',
  '44': 'Строителни материали и конструкции', '45': 'Строителни и монтажни работи',
  '48': 'Софтуерни пакети', '50': 'Услуги по ремонт и поддръжка',
  '51': 'Услуги по инсталиране', '55': 'Хотелиерство и ресторантьорство',
  '60': 'Транспортни услуги', '63': 'Спомагателни транспортни услуги',
  '64': 'Пощенски и телекомуникационни услуги', '65': 'Комунални услуги',
  '66': 'Финансови и застрахователни услуги', '70': 'Услуги с недвижими имоти',
  '71': 'Архитектурни и инженерни услуги', '72': 'ИТ услуги',
  '73': 'Научни изследвания и разработки', '75': 'Административни услуги',
  '76': 'Услуги за нефтената и газовата индустрия', '77': 'Услуги в земеделието и горите',
  '79': 'Бизнес и управленски услуги', '80': 'Образование и обучение',
  '85': 'Здравни и социални услуги', '90': 'Отпадъци и околна среда',
  '92': 'Култура, спорт и развлечения', '98': 'Други обществени услуги',
};

/* Bands are decades of the fixed euro peg. The bounds are in BGN because
   that is what value_bgn is normalised to, and they are repeated in the
   tenderFacets Cypher — the consistency test keeps the two in step. */
const PEG = 1.95583;
export const VALUE_BANDS = [
  { value: '0', label: 'до 10 хил. €', lo: null, hi: 10_000 * PEG },
  { value: '1', label: '10 хил. – 100 хил. €', lo: 10_000 * PEG, hi: 100_000 * PEG },
  { value: '2', label: '100 хил. – 1 млн. €', lo: 100_000 * PEG, hi: 1_000_000 * PEG },
  { value: '3', label: '1 млн. – 10 млн. €', lo: 1_000_000 * PEG, hi: 10_000_000 * PEG },
  { value: '4', label: 'над 10 млн. €', lo: 10_000_000 * PEG, hi: null },
] as const;

const EU_OPTIONS = [
  { value: '', label: 'Всички' },
  { value: 'true', label: 'Само с финансиране от ЕС' },
  { value: 'false', label: 'Само без финансиране от ЕС' },
] as const;

/* Indicators worth a single click. Each is a plain "yes" — there is no
   "not single-bid" filter, because the absence of a published bid count is
   not evidence of competition. */
const FLAGS = [
  { key: 'single', label: 'единствена оферта', countsFrom: 'singleOffer', countValue: 'true' },
  { key: 'shared', label: 'няколко изпълнителя' },
  { key: 'subcontracted', label: 'с подизпълнители' },
  { key: 'framework', label: 'рамково споразумение' },
] as const;

type FlagKey = (typeof FLAGS)[number]['key'];

const LIST_KEYS = ['cpv', 'sector', 'procedures', 'years', 'contractTypes',
  'buyerTypes', 'provinces'] as const;
type ListKey = (typeof LIST_KEYS)[number];

export type TenderState =
  & Record<ListKey, string[]>
  & Record<FlagKey, boolean>
  & { search: string; eu: string; band: string };

/** Fixes a list to one dossier: a buyer, or a winning company. */
export interface TenderScope {
  /** Procurement serving these municipalities (COMMISSIONED_BY). */
  buyerKeys?: string[];
  /** Procurement awarded by these bodies (PROCURED_BY). A ministry
      buying for a municipality answers to both, so they are separate
      scopes rather than one "buyer". */
  buyerUics?: string[];
  winnerUics?: string[];
}

export const TENDER_GROUPS = [
  { key: 'cpv', title: 'Сектор (CPV)', kind: 'tree', counts: 'cpv' },
  { key: 'procedures', title: 'Вид процедура', kind: 'check', counts: 'procedures' },
  { key: 'years', title: 'Година', kind: 'check', counts: 'years' },
  { key: 'eu', title: 'Финансиране от ЕС', kind: 'radio', options: EU_OPTIONS, counts: 'euFunded' },
  { key: 'band', title: 'Стойност (в евро)', kind: 'radio', options: VALUE_BANDS, counts: 'valueBands', all: true },
  { key: 'contractTypes', title: 'Вид на договора', kind: 'check', counts: 'contractTypes' },
  { key: 'buyerTypes', title: 'Вид възложител', kind: 'check', counts: 'buyerTypes' },
  { key: 'provinces', title: 'Област на възложителя', kind: 'check', counts: 'provinces' },
  { key: 'flags', title: 'Индикатори', kind: 'flags', items: FLAGS },
] as const;

function emptyState(): TenderState {
  const state = { search: '', eu: '', band: '' } as TenderState;
  for (const key of LIST_KEYS) state[key] = [];
  for (const flag of FLAGS) state[flag.key] = false;
  return state;
}

/** Every division a selection implies: chosen sectors plus loose ones. */
export function selectedDivisions(state: TenderState): string[] {
  const divisions = new Set(state.cpv);
  for (const key of state.sector) {
    const sector = CPV_SECTORS.find((entry) => entry.key === key);
    for (const leaf of sector?.leaves ?? []) divisions.add(leaf);
  }
  return [...divisions];
}

/**
 * Builds the tender facet set, optionally fixed to one dossier.
 *
 * The scope is folded into both the filter and the where, so a nested list
 * cannot show a count from outside its dossier — and because it is applied
 * in both, the consistency guarantee holds for nested lists too.
 */
export function tenderFacetSet(
  prefix = 't.',
  scope: TenderScope = {},
): FacetSet<TenderState, Record<string, unknown>, Record<string, unknown>> {
  return {
    prefix,
    groups: TENDER_GROUPS,
    taxonomy: {
      group: 'cpv',
      branches: CPV_SECTORS,
      leafLabels: DIVISION_LABELS,
      branchKey: 'sector',
    },
    emptyState,

    fromParams: (get, getAll) => {
      const state = emptyState();
      state.search = get('q') ?? '';
      state.eu = get('eu') ?? '';
      state.band = get('band') ?? '';
      for (const key of LIST_KEYS) state[key] = getAll(key);
      for (const flag of FLAGS) state[flag.key] = get(flag.key) === '1';
      return state;
    },

    toParams: (state) => {
      const params: Record<string, string | string[] | null> = {
        q: state.search || null,
        eu: state.eu || null,
        band: state.band || null,
      };
      for (const key of LIST_KEYS) params[key] = state[key].length ? state[key] : null;
      for (const flag of FLAGS) params[flag.key] = state[flag.key] ? '1' : null;
      return params;
    },

    activeCount: (state) => {
      let active = LIST_KEYS.reduce((sum, key) => sum + (state[key].length ? 1 : 0), 0);
      if (state.eu) active += 1;
      if (state.band) active += 1;
      return active + FLAGS.filter((flag) => state[flag.key]).length;
    },

    toFilter: (state) => {
      const band = VALUE_BANDS.find((entry) => entry.value === state.band);
      return {
        search: state.search || null,
        years: state.years.map(Number).filter(Number.isFinite),
        cpv: selectedDivisions(state),
        procedures: state.procedures,
        contractTypes: state.contractTypes,
        buyerTypes: state.buyerTypes,
        provinces: state.provinces,
        euFunded: state.eu === '' ? null : state.eu === 'true',
        valueBand: band ? Number(band.value) : null,
        singleOffer: state.single ? true : null,
        subcontracted: state.subcontracted ? true : null,
        shared: state.shared ? true : null,
        framework: state.framework ? true : null,
        ...(scope.buyerKeys?.length ? { buyerKeys: scope.buyerKeys } : {}),
        ...(scope.buyerUics?.length ? { buyerUics: scope.buyerUics } : {}),
        ...(scope.winnerUics?.length ? { winnerUics: scope.winnerUics } : {}),
      };
    },

    toWhere: (state) => {
      /* Never a row of its own: a TED notice repeating a ЦАИС ЕОП contract
         is the same procurement published twice, and it used to appear
         beside the original with a different title and the same money.
         Unconditional, so it holds for the dossier lists too — this is the
         only place the tender tables are filtered from.

         Not the same treatment as `value_excluded`, which stays in the
         table with a warning: a tender whose figure cannot be trusted is
         still a procurement that happened. */
      const clauses: Record<string, unknown>[] = [{ duplicate_of: null }];
      if (scope.buyerKeys?.length) {
        clauses.push({ commissionedBy_SOME: { key_IN: scope.buyerKeys } });
      }
      if (scope.buyerUics?.length) {
        clauses.push({ procuredBy_SOME: { uic_IN: scope.buyerUics } });
      }
      if (scope.winnerUics?.length) {
        clauses.push({ winners_SOME: { uic_IN: scope.winnerUics } });
      }
      if (state.search) {
        clauses.push({
          OR: [
            { title_CONTAINS: state.search },
            { procuring_entity_CONTAINS: state.search },
            { tender_id_CONTAINS: state.search },
          ],
        });
      }
      // a year is a half-open range on the ISO publication date; several
      // years are a union of those ranges
      if (state.years.length) {
        clauses.push({
          OR: state.years.map((year) => ({
            publication_date_GTE: `${year}-01-01`,
            publication_date_LT: `${Number(year) + 1}-01-01`,
          })),
        });
      }
      const divisions = selectedDivisions(state);
      if (divisions.length) {
        clauses.push({ OR: divisions.map((d) => ({ cpv_code_STARTS_WITH: d })) });
      }
      if (state.procedures.length) clauses.push({ procedure_type_IN: state.procedures });
      if (state.contractTypes.length) clauses.push({ contract_type_IN: state.contractTypes });
      if (state.buyerTypes.length) clauses.push({ buyer_type_IN: state.buyerTypes });
      if (state.provinces.length) {
        clauses.push({ commissionedBy_SOME: { province_IN: state.provinces } });
      }
      if (state.eu) clauses.push({ is_eu_funded: state.eu === 'true' });
      const band = VALUE_BANDS.find((entry) => entry.value === state.band);
      if (band) {
        const clause: Record<string, number> = {};
        if (band.lo != null) clause['value_bgn_GTE'] = band.lo;
        if (band.hi != null) clause['value_bgn_LT'] = band.hi;
        clauses.push(clause);
      }
      if (state.single) clauses.push({ offers_count: 1 });
      if (state.shared) clauses.push({ winner_count_GT: 1 });
      if (state.subcontracted) clauses.push({ has_subcontractors: true });
      if (state.framework) clauses.push({ is_framework: true });

      return clauses.length === 1 ? clauses[0]! : { AND: clauses };
    },
  };
}
